import moment from "moment-timezone"
import { Article, HandlingUnit, TimeWindow } from "@/model/transportorder/transportOrder"
import { pullUpArticles } from "@/model/transportorder/transportOrderFlattening"
import { normalizeToKgm, normalizeToMtr } from "@/model/transportorder/measurement"
import { IntlShape } from "react-intl"
import Papa from "papaparse"
import { saveAs } from "file-saver"

export const toHumanReadableDate = (locale: string, dateString?: string, timezone: string | undefined = "UTC"): string | undefined => {
    if (!dateString || !timezone) {
        return undefined
    }
    return moment(dateString).tz(timezone).locale(locale).format("L")
}

export const toHumanReadableTime = (locale: string, dateString?: string, timezone: string | undefined = "UTC"): string | undefined => {
    if (!dateString || !timezone) {
        return undefined
    }
    return moment(dateString).tz(timezone).locale(locale).format("LT")
}

export const toHumanReadableDateAndTime = (locale: string, dateString?: string, timezone: string | undefined = "UTC"): string | undefined => {
    if (!dateString || !timezone) {
        return undefined
    }
    const localizedDate = moment(dateString).tz(timezone).locale(locale)
    return `${localizedDate.format("L")} ${localizedDate.format("LT")}`
}

export const toHumanReadableTimeWindow = (locale: string, timeWindow?: TimeWindow, timezone: string | undefined = "UTC"): string | undefined => {
    if (!timeWindow || !timezone) {
        return undefined
    }
    const startDateTime = timeWindow.time_from && moment(timeWindow.time_from).tz(timezone).locale(locale)
    const endDateTime = moment(timeWindow.time_to).tz(timezone).locale(locale)
    const formattedEndDateTime = `${endDateTime.format("L")} ${endDateTime.format("LT")}`
    if (startDateTime && !startDateTime.isSame(endDateTime)) {
        const formattedStartDateTime =  `${startDateTime.format("L")} ${startDateTime.format("LT")}`
        return `${formattedStartDateTime} - ${formattedEndDateTime}`
    } else {
        return formattedEndDateTime
    }
}

export interface CsvHeader<Row extends object> {
    key: keyof Row
    label: string
}

export const serializeCsv = <Row extends object>(
    rows: Row[],
    headers: CsvHeader<Row>[],
    intl: IntlShape,
): string => {
    const fields = headers.map(header => header.label)
    const data = rows.map(row => headers.map(header => formatCsvValue(row[header.key], intl)))

    return `\uFEFF${Papa.unparse(
        { fields, data },
        {
            delimiter: ",",
            newline: "\n",
            quotes: true,
            escapeFormulae: /^[=+\-@\t\r]/,
        },
    )}`
}

export const downloadCsv = <Row extends object>(
    rows: Row[],
    headers: CsvHeader<Row>[],
    filename: string,
    intl: IntlShape,
): void => {
    const csv = serializeCsv(rows, headers, intl)
    saveAs(new Blob([csv], { type: "text/csv;charset=utf-8" }), filename)
}

const formatCsvValue = (value: unknown, intl: IntlShape): unknown =>
    typeof value === "number"
        ? value.toLocaleString(intl.locale, { maximumFractionDigits: 5 })
        : value ?? ""

export const calculateVolume = (handlingUnit: HandlingUnit): number | undefined => {
    const normalizedWidth = normalizeToMtr(handlingUnit.dimension?.width)
    const normalizedLength = normalizeToMtr(handlingUnit.dimension?.length)
    const normalizedHeight = normalizeToMtr(handlingUnit.dimension?.height)
    if (!normalizedWidth || !normalizedWidth.value ||
        !normalizedLength || !normalizedLength.value ||
        !normalizedHeight || !normalizedHeight.value) {
        return undefined
    }

    const volumePerHandlingUnitInMTQ = normalizedWidth.value * normalizedLength.value * normalizedHeight.value
    const quantity = handlingUnit.quantity ?? 1

    return (quantity * volumePerHandlingUnitInMTQ)
}

export const calculateWeight = (handlingUnit: HandlingUnit): number | undefined => {
    const normalizedWeight = normalizeToKgm(handlingUnit.weight)
    if (!normalizedWeight || !normalizedWeight.value) {
        return undefined
    }

    const quantity = handlingUnit.quantity ?? 1
    return (quantity * normalizedWeight.value)
}

export const aggregateNetExplosiveMassInG = (handlingUnit: HandlingUnit): number | undefined => {

    const multiply = (a: number | undefined, b: number | undefined): number | undefined => a !== undefined && b !== undefined ? a * b : undefined
    const add = (a: number | undefined, b: number | undefined): number | undefined => a !== undefined ? b !== undefined ? a + b : a : b

    const articles = pullUpArticles(handlingUnit)
    const nemArticlesAggregated = articles.content.reduce<number | undefined>(((previousValue, currentArticle) => {
        const currentNemKg = normalizeToKgm(currentArticle.net_explosive_mass)
        return add(previousValue, multiply(currentNemKg?.value, currentArticle.quantity))
    }), undefined)
    const nemTotal = multiply(nemArticlesAggregated, handlingUnit.quantity)
    return multiply(nemTotal, 1000)
}

export const hasCustomsGoods = (articles: Article[], intl: IntlShape) : string => {
    const hasDutiableGoods = articles.some(article => article.country_of_origin?.dutiable_goods === true)
    return hasDutiableGoods ? intl.formatMessage({ id: "yes" }) : intl.formatMessage({ id: "no" })
}
